import "./dialog.script";
import { RUI_DIALOG_ACTION_EVENT, RUI_DIALOG_CLOSE_EVENT, RUI_DIALOG_HOST_ID, RUI_DIALOG_OPEN_EVENT, RUI_DIALOG_TOGGLE_EVENT, isNamedDialogOpen } from "./dialog-types";
let installed = false;
let mountParent = null;
let hostDialog = null;
let openId = null;
function resolveDialog(id) {
  const trimmed = id.trim();
  if (!trimmed) {
    return null;
  }
  const byId = document.getElementById(trimmed);
  if (byId?.localName === "rui-dialog") {
    return byId;
  }
  return null;
}
function createCloseButton() {
  const close2 = document.createElement("button");
  close2.type = "button";
  close2.setAttribute("data-dialog-close", "");
  close2.setAttribute("data-ref", "close");
  close2.className = "rui-dialog__close";
  close2.setAttribute("aria-label", "Close");
  close2.textContent = "\xD7";
  return close2;
}
function createTitle(title) {
  const el = document.createElement("div");
  el.setAttribute("data-dialog-title", "");
  el.setAttribute("data-ref", "title");
  el.className = "rui-dialog__title";
  el.textContent = title;
  return el;
}
function createBody(body) {
  const el = document.createElement("div");
  el.setAttribute("data-dialog-body", "");
  el.setAttribute("data-ref", "description");
  el.className = "rui-dialog__body";
  const paragraph = document.createElement("p");
  paragraph.textContent = body;
  el.append(paragraph);
  return el;
}
function createActions(actions) {
  const row = document.createElement("div");
  row.className = "rui-dialog__actions";
  for (const action of actions) {
    const button = document.createElement("button");
    button.type = "button";
    button.className = `rui-button rui-button--${action.variant ?? "filled"} rui-button--${action.size ?? "md"}`;
    button.setAttribute("data-dialog-action", action.value ?? "");
    if (action.closeOnClick === false) {
      button.setAttribute("data-dialog-action-close", "false");
    }
    button.textContent = action.label;
    row.append(button);
  }
  return row;
}
function createDialogSurface(alert) {
  const root = document.createElement("div");
  root.dataset.ref = "root";
  root.className = "rui-dialog";
  const backdrop = document.createElement("div");
  backdrop.setAttribute("data-ref", "backdrop");
  backdrop.className = "rui-dialog__backdrop";
  const surface = document.createElement("div");
  surface.setAttribute("data-ref", "dialog");
  surface.className = "rui-dialog__surface";
  surface.tabIndex = -1;
  surface.setAttribute("role", alert ? "alertdialog" : "dialog");
  surface.setAttribute("aria-modal", "true");
  root.append(backdrop, surface);
  return root;
}
function destroyHostDialog() {
  hostDialog?.remove();
  hostDialog = null;
}
function mountHostDialog(options) {
  destroyHostDialog();
  const dialog = document.createElement("rui-dialog");
  dialog.id = RUI_DIALOG_HOST_ID;
  dialog.alert = Boolean(options.alert);
  dialog.label = options.label ?? "";
  dialog.open = true;
  const shell = createDialogSurface(dialog.alert);
  const surface = shell.querySelector('[data-ref="dialog"]');
  if (!surface) {
    throw new Error("Failed to create the dialog surface.");
  }
  surface.append(createCloseButton());
  if (options.title) {
    surface.append(createTitle(options.title));
  }
  if (options.body) {
    surface.append(createBody(options.body));
  }
  if (options.actions?.length) {
    surface.append(createActions(options.actions));
  }
  dialog.append(shell);
  (mountParent ?? document.body).appendChild(dialog);
  hostDialog = dialog;
  return dialog;
}
function closeCurrentExcept(nextId) {
  if (!openId || openId === nextId) {
    return;
  }
  if (openId === RUI_DIALOG_HOST_ID) {
    destroyHostDialog();
  } else {
    const dialog = resolveDialog(openId);
    if (dialog) {
      dialog.open = false;
    }
  }
}
function openNamed(id) {
  ensureInstalled();
  const dialog = resolveDialog(id);
  if (!dialog) {
    return;
  }
  closeCurrentExcept(id);
  dialog.open = true;
  openId = id;
}
function openHost(options) {
  ensureInstalled();
  closeCurrentExcept(RUI_DIALOG_HOST_ID);
  mountHostDialog(options);
  openId = RUI_DIALOG_HOST_ID;
}
function open(idOrOptions) {
  if (typeof idOrOptions === "string") {
    openNamed(idOrOptions);
    return;
  }
  if (isNamedDialogOpen(idOrOptions)) {
    openNamed(idOrOptions.id);
    return;
  }
  openHost(idOrOptions);
}
function close(id) {
  ensureInstalled();
  const targetId = id ?? openId;
  if (!targetId) {
    return;
  }
  if (targetId === RUI_DIALOG_HOST_ID) {
    destroyHostDialog();
  } else {
    const dialog = resolveDialog(targetId);
    if (dialog) {
      dialog.open = false;
    }
  }
  if (openId === targetId) {
    openId = null;
  }
}
function toggle(id) {
  ensureInstalled();
  if (openId === id) {
    close(id);
    return;
  }
  openNamed(id);
}
function onOpenEvent(event) {
  const detail = event.detail;
  if (typeof detail === "string") {
    open(detail);
    return;
  }
  if (detail && typeof detail === "object") {
    open(detail);
  }
}
function onCloseEvent(event) {
  const detail = event.detail;
  close(detail?.id);
}
function onToggleEvent(event) {
  const detail = event.detail;
  if (detail?.id) {
    toggle(detail.id);
  }
}
function onDocumentClick(event) {
  const target = event.target;
  if (!target?.closest) {
    return;
  }
  const action = target.closest("[data-dialog-action]");
  if (action && hostDialog?.contains(action)) {
    event.preventDefault();
    const value = action.getAttribute("data-dialog-action") || void 0;
    const detail = {
      value: value || void 0
    };
    hostDialog.dispatchEvent(new CustomEvent(RUI_DIALOG_ACTION_EVENT, {
      detail,
      bubbles: true,
      composed: true
    }));
    if (action.getAttribute("data-dialog-action-close") !== "false") {
      close(RUI_DIALOG_HOST_ID);
    }
    return;
  }
  const openTrigger = target.closest("[data-dialog-open]");
  if (openTrigger) {
    const id = openTrigger.getAttribute("data-dialog-open")?.trim();
    if (id) {
      event.preventDefault();
      openNamed(id);
    }
    return;
  }
  const closeTrigger = target.closest("[data-dialog-close]");
  if (closeTrigger) {
    const insideDialog = closeTrigger.closest("rui-dialog");
    if (insideDialog?.hasAttribute("open")) {
      return;
    }
    event.preventDefault();
    close();
  }
}
function onRuiClose(event) {
  const dialog = event.target?.closest?.("rui-dialog");
  if (!dialog) {
    return;
  }
  const id = dialog.id || null;
  if (id && openId === id) {
    if (id === RUI_DIALOG_HOST_ID) {
      destroyHostDialog();
    }
    openId = null;
  }
}
export function installDialogs(options = {}) {
  if (installed) {
    if (options.mount) {
      mountParent = options.mount;
    }
    return uninstallDialogs;
  }
  installed = true;
  mountParent = options.mount ?? document.body;
  document.addEventListener(RUI_DIALOG_OPEN_EVENT, onOpenEvent);
  document.addEventListener(RUI_DIALOG_CLOSE_EVENT, onCloseEvent);
  document.addEventListener(RUI_DIALOG_TOGGLE_EVENT, onToggleEvent);
  document.addEventListener("click", onDocumentClick);
  document.addEventListener("rui-close", onRuiClose);
  return uninstallDialogs;
}
function ensureInstalled() {
  if (!installed) {
    installDialogs();
  }
}
export function uninstallDialogs() {
  if (!installed) {
    return;
  }
  document.removeEventListener(RUI_DIALOG_OPEN_EVENT, onOpenEvent);
  document.removeEventListener(RUI_DIALOG_CLOSE_EVENT, onCloseEvent);
  document.removeEventListener(RUI_DIALOG_TOGGLE_EVENT, onToggleEvent);
  document.removeEventListener("click", onDocumentClick);
  document.removeEventListener("rui-close", onRuiClose);
  destroyHostDialog();
  mountParent = null;
  openId = null;
  installed = false;
}
export function getOpenDialogId() {
  return openId;
}
export function openDialog(idOrOptions) {
  ensureInstalled();
  open(idOrOptions);
}
export function closeDialog(id) {
  ensureInstalled();
  close(id);
}
export function toggleDialog(id) {
  ensureInstalled();
  toggle(id);
}
export function createDialogHandle(id) {
  ensureInstalled();
  const boundId = id?.trim() || void 0;
  const handleOpen = idOrOptions => {
    if (boundId) {
      openNamed(boundId);
      return;
    }
    if (idOrOptions == null) {
      return;
    }
    if (typeof idOrOptions === "string") {
      openNamed(idOrOptions);
      return;
    }
    open(idOrOptions);
  };
  return {
    open: handleOpen,
    close: closeId => {
      close(closeId ?? boundId);
    },
    toggle: toggleId => {
      const target = toggleId ?? boundId;
      if (target) {
        toggle(target);
      }
    },
    get openId() {
      return openId;
    }
  };
}